import React, { useEffect, useMemo, useState } from 'react';
import { type Location, Navigate, Route, Routes, useLocation, useParams, useNavigate } from 'react-router-dom';
import { BrandComponent, useBrandComponent } from '../brands/useBrandComponentHook';
import Layout from '../layout';
import { APP_URL, DEFAULT_LANGUAGE, DEFAULT_COUNTRY, DEFAULT_COUNTRY_LANGUAGE_SEPARATOR, LOCAL_STORAGE_KEYS, SESSION_STORAGE_KEYS } from '../project/defines';
import { type RootState } from '../store';
import { type AuthModel } from '../store/auth/authModels';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { hasWarrantyAccess } from '../utils/helpers';
import { getSessionStorageItem, getSessionStorageItemTtl, removeSessionStorageItem, /*setSessionStorageItem,*/ setSessionStorageItemTtl } from '../utils/helpers/sessionStorage';
import { correctExternalUrl, getApiUrl, getUniversFromUrl, loginConnectAs, downloadFromUrl, redirectToUrlForUser, navigateToUrl, getActiveCountries, correctUrl } from '../project/helpers';
import UserClients from './account/clients';
import Profile from './account/profile';
import Societe from './account/societe';
import ArticleDetails from './article';
import Family from './family';
import OrderHistory from './orderHistory';
import OrderDetail from './orderHistory/orderDetails';
import Product from './product';
import QuoteHistory from './quoteHistory';
import { SearchResults } from './search/search';
import ShoppingCart from './shoppingCart';
import Template from './template';
import WarrantyHistory from './warrantyHistory';
import WarrantyRequest from './warrantyRequest';
import { type Univers } from '../store/univers/universModels';
import Loader from '../components/shared/loader';
import { authApi } from '../store/auth/authApi';
import { shoppingCartApi } from '../store/shoppingCart/shoppingCartApi';
import { reloadIsLoggedInFromStorage } from '../store/auth/authSlice';
import { getLocalStorageItem, setLocalStorageItem } from '../utils/helpers/localStorage';
import { setCurrentLanguage } from '../store/language/languageSlice';
import { setCurrentCountry } from '../store/sideData/siteDataSlice';
import { useGetCountryQuery } from '../store/country/countryApi';
import { useGetLanguagesQuery } from '../store/language/laguageApi';
import { useAuth } from 'utils/hooks/useAuth';
import { addNotification } from 'store/componentsSlice';
import useTranslation from 'utils/hooks/useTranslation';

const AppRoutes: React.FC = () => {
  const language = useAppSelector((state) => state.language.currentLanguage) || DEFAULT_LANGUAGE;
  const country = useAppSelector((state) => state.siteData.currentCountry?.code) || DEFAULT_COUNTRY;
  const { data: countries } = useGetCountryQuery(language)
  const activeCountries = getActiveCountries(countries)
  const location = useLocation();
  const state = useAppSelector((state) => state);
  const authUser = useAppSelector((state) => state.auth);
  //const languages = useAppSelector((state) => state.language.languages)
  const univers = useAppSelector((state) => state.siteData.universInfo?.univers);
  const { data: languages } = useGetLanguagesQuery();

  const Home = useBrandComponent(BrandComponent.Home);

  const startsWithLangCode = useMemo(() => {
    return (languages ?? [{ lang: 'fr' }])?.some((lang) => location.pathname.startsWith(`/${lang.lang}`) || location.pathname.startsWith(`/${country}${DEFAULT_COUNTRY_LANGUAGE_SEPARATOR}${lang.lang}`));
  }, [languages, country, location.pathname]);
  if (!(activeCountries && languages)){
    return (
      <Routes>
      <Route path={APP_URL.RedirectResource} element={<ResourceRedirect/>}/>
      <Route path={APP_URL.Base} element={<Layout hasBrandLayout={false} />}>
        <Route path={APP_URL.Template} element={<Template />} />
      </Route>

      <Route path={APP_URL.WsoCallback} element={<WsoCallbackRedirect />} />
      <Route path={APP_URL.WebStatonRedirect} element={<WebStationRedirect />} />
      <Route path={APP_URL.ConnectAs} element={<ConnectAs />} />
      <Route path={APP_URL.Logout} element={<LogoutComponentRedirect />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path={APP_URL.RedirectResource} element={<ResourceRedirect />} />
      <Route path={APP_URL.Base} element={<Layout hasBrandLayout={false} />}>
        <Route path={APP_URL.Template} element={<Template />} />
      </Route>
      <Route path={APP_URL.Base} element={<Layout />}>
        <Route path="" element={<Home />} />
        <Route path={APP_URL.Login} element={<LoginComponentRedirect />} />

        {getAppRoutes(location, authUser, language, country, state, univers)}
      </Route>
      <Route path={APP_URL.Logout} element={<LogoutComponentRedirect />} />
      <Route path={APP_URL.WsoCallback} element={<WsoCallbackRedirect />} />
      <Route path={APP_URL.WebStatonRedirect} element={<WebStationRedirect />} />
      <Route path={APP_URL.ConnectAs} element={<ConnectAs />} />
      {getAppRoutes(location, authUser, language, country, state, univers, false)}
      <Route path="/" element={<Navigate to={`/${country}${DEFAULT_COUNTRY_LANGUAGE_SEPARATOR}${language}`} replace={true} />} />
      <Route
        path="*"
        element={
          <Navigate
            to={startsWithLangCode ? `/${country}${DEFAULT_COUNTRY_LANGUAGE_SEPARATOR}${language}` : `/${country}${DEFAULT_COUNTRY_LANGUAGE_SEPARATOR}${language}${location.pathname + location.search}`}
            replace={true}
          />
        }
      />
    </Routes>
  );
};
export default AppRoutes;

const LoginComponentRedirect = () => {
  const { lang } = useParams();
  return <Navigate to={`/${lang}`} replace={true} />;
};

const LogoutComponentRedirect = () => {
  const auth = useAuth();

  useEffect(() => {
    auth.logout();
  }, [auth]);
  return <Loader />;
};

const ResourceRedirect = () => {
  const location = useLocation();
  const searchPararms = new URLSearchParams(location.search);
  const url = searchPararms.get("url");
  const [dowloaded, setDownloaded] = useState<boolean>(false);

  useEffect(() => {
    if (url && !dowloaded) {
      downloadFromUrl(url, (data: any) => {
        setDownloaded(true);
        globalThis.location.href = data;
        if (url.includes('/export')) {
          setTimeout(() => {
            globalThis.close();
          }, 200);
        }
      })

    }
  }, []);
  return <Loader />;
};

const WsoCallbackRedirect = () => {
  const location = useLocation();
  const searchPararms = new URLSearchParams(location.search);
  const state = searchPararms.get("state");
  const language = useAppSelector((state) => state.language.currentLanguage);
  const country = useAppSelector((state) => state.siteData?.currentCountry?.code) || DEFAULT_COUNTRY;
  const { data: countries } = useGetCountryQuery(language);
  const activeCountries = getActiveCountries(countries);
  const [allowNavigate, setAllowNavigate] = useState<boolean>(false);
  const [localLang, setLocalLang] = useState<string>();
  const [localCountry, setLocalCountry] = useState<string>();
  const previousURL = getSessionStorageItem(SESSION_STORAGE_KEYS.PreviousURL);
  const dispatch = useAppDispatch();
  const auth = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    if (allowNavigate && localCountry && localCountry === country) {
      let lang = DEFAULT_LANGUAGE;
      if (localLang && localLang === language) {
        lang = localLang;
      }
      //debugger
      if (previousURL) {
        const path = previousURL.slice(1)
          .split('/')
          .filter((_p, i) => i > 0)
          .join('/');
        removeSessionStorageItem(SESSION_STORAGE_KEYS.PreviousURL);
        navigateToUrl(correctUrl(`/${localCountry}${DEFAULT_COUNTRY_LANGUAGE_SEPARATOR}${lang}/${path}`))
      } else {
        navigateToUrl(correctUrl(`/${localCountry}${DEFAULT_COUNTRY_LANGUAGE_SEPARATOR}${lang}/login`))
      }
    }

  }, [allowNavigate, localLang, localCountry, language, country])

  useEffect(() => {
    if (!activeCountries || !countries || !state || getSessionStorageItemTtl("login")) {
      return;
    }

    setSessionStorageItemTtl("login", true, 30);

    auth.checkUser(state).then((data: any) => {
      console.log(data);
      if (data.status === "success") {
        setLocalStorageItem(LOCAL_STORAGE_KEYS.SessionCode, data.session_code);
        dispatch(authApi.util.invalidateTags(['LoggedUser'])); // nice way to invalidate tags from another api
        dispatch(shoppingCartApi.util.invalidateTags(['CartList'])); // nice way to invalidate tags from another api
        dispatch(reloadIsLoggedInFromStorage());
        //navigate('/', { replace: true })
        if (data.culture) {
          dispatch(setCurrentLanguage(data.culture));
          setLocalLang(data.culture);
        }
        if (activeCountries != null) {
          dispatch(setCurrentCountry(activeCountries.find((item: any) => item.code === data.pays)));
          setLocalCountry(data.pays);
        }

        setAllowNavigate(true);

      } else if (data.message || data.error) {
        const message = data.message || data.error || "Unknown error";
        console.log(message);
        const t_message = message.includes("state_") ? message : t('api.error.' + message);
        dispatch(
              addNotification({
                type: 'error',
                message: t_message,
                description: t_message,
                duration: 5,
              })
            );

        //auth.logout();
        setTimeout(() => {  
          navigate('/', { replace: true })
          }, 1500);
      }
    })





  }, [activeCountries, state]);
  return <Loader />;
};


const WebStationRedirect = () => { // NOSONAR
  useEffect(() => {
    if (!getSessionStorageItemTtl("webstation")) {
      setSessionStorageItemTtl("webstation", true, 30);
      const univers = getUniversFromUrl();
      const apiUrl = getApiUrl(univers);
      const safeBackUrl = correctExternalUrl(`${globalThis.location.origin}/`);
      const backUrlParam = safeBackUrl.startsWith('http')
        ? safeBackUrl
        : `${globalThis.location.origin}${safeBackUrl}`;
      let url = `${apiUrl}/redirectWebStation?back_url=${encodeURIComponent(backUrlParam)}`;
      const session_code = getLocalStorageItem(LOCAL_STORAGE_KEYS.SessionCode);
      if (session_code) {
        url += `&session_code=${session_code}`;
      }
      redirectToUrlForUser(url);
    }
  }, []);
  return <Loader />;
};

const ConnectAs = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const language = useAppSelector((state) => state.language.currentLanguage);
  const country = useAppSelector((state) => state.siteData?.currentCountry?.code) || DEFAULT_COUNTRY;
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!code) {
      navigate('/', { replace: true });
    }
    if (!getSessionStorageItemTtl("login")) {
      setSessionStorageItemTtl("login", true, 30);
      let univers = getUniversFromUrl();
      let apiUrl = getApiUrl(univers);
      loginConnectAs(`${apiUrl}/public/connectas/${code}`,
        () => {
          dispatch(authApi.util.invalidateTags(['LoggedUser'])); // nice way to invalidate tags from another api
          dispatch(shoppingCartApi.util.invalidateTags(['CartList'])); // nice way to invalidate tags from another api
          dispatch(reloadIsLoggedInFromStorage());
          navigate('/', { replace: true });
          navigateToUrl(`/${country}${DEFAULT_COUNTRY_LANGUAGE_SEPARATOR}${language}`)
        },
        () => {
          navigate('/', { replace: true });
          navigateToUrl(`/${country}${DEFAULT_COUNTRY_LANGUAGE_SEPARATOR}${language}`)
        }

      );
    }
  }, []);
  return <Loader />;
};

const getAppRoutes = (
  location: Location,
  authUser: AuthModel,
  language: string,
  country: string,
  state: RootState,
  univers?: Univers,
  isInBaseRoute: boolean = true
) => {
  const isLoggedIn = !!authUser.uuid;

  if (!isLoggedIn && univers?.restricted) return null;

  const loggedInRoutes = [
    { path: APP_URL.ShoppingCart, component: <ShoppingCart /> },
    { path: APP_URL.ShoppingCartSuccess, component: <ShoppingCart /> },
    { path: APP_URL.WarrantyHistory, component: <WarrantyHistory /> },
    { path: APP_URL.OrderHistory, component: <OrderHistory /> },
    { path: APP_URL.OrderDetails, component: <OrderDetail /> },
    { path: APP_URL.Profile, component: <Profile /> },
    { path: APP_URL.Societe, component: <Societe /> },
    { path: APP_URL.Clients, component: <UserClients /> },
    { path: APP_URL.QuoteHistory, component: <QuoteHistory /> },
  ];

  hasWarrantyAccess(state) &&
    loggedInRoutes.push({ path: APP_URL.WarrantyReplacement, component: <WarrantyRequest /> });

  const mainRoutes = [
    { path: APP_URL.Search, component: <SearchResults /> },
    { path: APP_URL.Family, component: <Family /> },
    { path: APP_URL.FamilyDetail, component: <Family /> },
    { path: APP_URL.Product, component: <Product /> },
    { path: APP_URL.ProductSchema, component: <Product /> },
    { path: APP_URL.ProductReseau, component: <Product /> },
    { path: APP_URL.Article, component: <ArticleDetails /> },
    { path: APP_URL.ArticleReseau, component: <ArticleDetails /> },
  ];

  return (
    <>
      {isLoggedIn && (
        <>
          {loggedInRoutes.map((route) => {
            let lang_path = `/${country}${DEFAULT_COUNTRY_LANGUAGE_SEPARATOR}${language}`;
            if (!isInBaseRoute && location.pathname && location.pathname.includes(lang_path)) {
              lang_path = "";
            }
            return <Route
              key={route.path}
              path={route.path}
              element={
                isInBaseRoute ? (
                  route.component
                ) : (
                  <Navigate to={`${lang_path + location.pathname + location.search}`} replace={true} />
                )
              }
            />
          })}
        </>
      )}
      {mainRoutes.map((route) => {
        let lang_path = `/${country}${DEFAULT_COUNTRY_LANGUAGE_SEPARATOR}${language}`;
        if (!isInBaseRoute && location.pathname && location.pathname.includes(lang_path)) {
          lang_path = "";
        }
        return <Route
          key={route.path}
          path={route.path}
          element={
            isInBaseRoute ? (
              route.component
            ) : (
              <Navigate to={`${lang_path + location.pathname + location.search}`} replace={true} />
            )
          }
        />
      })}
    </>
  );
};
